import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Calculator, Package, TrendingDown, Check, Mail, Phone, X, User, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BulkPricingPage = () => {
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState('onyx-lighting');
  const [quantity, setQuantity] = useState(50);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [emailForm, setEmailForm] = useState({
    name: '',
    email: '',
    phone: '',
    quantity: '',
    message: ''
  });

  const products = [
    { 
      id: 'onyx-lighting', 
      name: 'ONYX LED Potlight', 
      basePrice: 12.99,
      available: true,
      description: '5-CCT Adjustable LED Potlight'
    },
    { 
      id: 'onyx-hvac', 
      name: 'Onyx HVAC', 
      basePrice: 0,
      available: false,
      description: 'Coming Q2 2026'
    }
  ];

  const pricingTiers = [
    { min: 1, max: 99, discount: 0, pricePerUnit: 12.99, label: 'Retail', showPrice: true },
    { min: 100, max: 199, discount: 8, pricePerUnit: 11.99, label: 'Contractor', showPrice: true },
    { min: 200, max: 499, discount: 0, pricePerUnit: 0, label: 'Partner', showPrice: false, action: 'quote' },
    { min: 500, max: null, discount: 0, pricePerUnit: 0, label: 'Distributor', showPrice: false, action: 'quote' }
  ];

  const currentProduct = products.find(p => p.id === selectedProduct);
  const currentTier = pricingTiers.find(tier => 
    quantity >= tier.min && (tier.max === null || quantity <= tier.max)
  ) || pricingTiers[0];

  const calculateTotal = () => {
    if (!currentProduct.available || !currentTier.showPrice) return null;
    return (currentTier.pricePerUnit * quantity).toFixed(2);
  };

  const calculateSavings = () => {
    if (!currentProduct.available || !currentTier.showPrice) return 0;
    const regularPrice = currentProduct.basePrice * quantity;
    const discountedPrice = currentTier.pricePerUnit * quantity;
    return (regularPrice - discountedPrice).toFixed(2);
  };

  const handleEmailFormChange = (e) => {
    setEmailForm({
      ...emailForm,
      [e.target.name]: e.target.value
    });
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    // Simulate email send
    toast.success('Quote request sent! We\'ll contact you within 24 hours.');
    setShowEmailPopup(false);
    setEmailForm({ name: '', email: '', phone: '', quantity: '', message: '' });
  };

  const handleCTAClick = () => {
    if (!currentProduct.available) {
      toast.info('This product will be available Q2 2026');
      return;
    }

    if (currentTier.action === 'quote') {
      setEmailForm({ ...emailForm, quantity: quantity.toString() });
      setShowEmailPopup(true);
    } else {
      navigate('/products');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-lighting/artifacts/27f7yllt_WhatsApp%20Image%202025-10-18%20at%2008.58.35.jpeg" 
                alt="ONYX Home Solutions" 
                className="h-12 lg:h-16 w-auto object-contain"
                style={{ background: 'transparent' }}
              />
            </button>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Home
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Products
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/bulk-pricing'); }} className="text-sm font-medium transition-colors" style={{ color: '#8B6F47' }}>
                Bulk
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Guides
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Become a Partner
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/admin/login'); }} className="text-sm font-medium transition-colors" style={{ color: '#8B6F47' }}>
                Login
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/cart'); }} className="relative p-2 rounded-full hover:bg-gray-100 transition-colors" style={{ color: '#8B6F47' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </button>
            </div>
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => {
                const menu = document.getElementById('mobile-menu');
                menu.classList.toggle('hidden');
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        <div id="mobile-menu" className="hidden md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 space-y-1">
            <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Home
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Products
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/bulk-pricing'); }} className="block w-full text-left px-3 py-2 text-sm text-amber-600 hover:bg-gray-50 rounded-lg">
              Bulk
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Guides
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              Become a Partner
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/admin/login'); }} className="block w-full text-left px-3 py-2 text-sm text-amber-600 hover:bg-gray-50 rounded-lg">
              Login
            </button>
            <button onClick={() => { window.scrollTo(0, 0); navigate('/cart'); }} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Cart
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #D4A574 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 text-center text-white">
          <div className="inline-block mb-6 px-6 py-2 rounded-full" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
            <span className="text-sm font-bold">VOLUME DISCOUNTS</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold mb-6">
            Bulk Pricing
          </h1>
          <p className="text-xl lg:text-2xl text-white/80 max-w-3xl mx-auto leading-relaxed mb-8">
            Professional pricing for contractors, electricians, and distributors. The more you buy, the more you save.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20">
              <Check className="w-5 h-5" />
              <span className="font-medium">Up to 30% Off</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20">
              <Check className="w-5 h-5" />
              <span className="font-medium">Fast Shipping</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20">
              <Check className="w-5 h-5" />
              <span className="font-medium">Dedicated Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Bulk Pricing Calculator */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Calculator */}
            <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold" style={{ color: '#1d1d1f' }}>Price Calculator</h2>
              </div>

              {/* Product Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Product
                </label>
                <select 
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none text-base"
                  style={{ borderColor: '#D4A574', focusBorderColor: '#8B6F47' }}
                >
                  {products.map(product => (
                    <option key={product.id} value={product.id} disabled={!product.available}>
                      {product.name} - {product.description}
                    </option>
                  ))}
                </select>
              </div>

              {currentProduct.available ? (
                <>
                  {/* Quantity Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Quantity
                    </label>
                    <input 
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none text-base font-semibold"
                      style={{ borderColor: '#D4A574' }}
                    />
                    <div className="flex flex-wrap gap-2 mt-3">
                      {[50, 150, 300, 600].map(qty => (
                        <button
                          key={qty}
                          onClick={() => setQuantity(qty)}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                          style={{ background: '#fef3e4', color: '#8B6F47' }}
                        >
                          {qty}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Current Tier */}
                  <div className="rounded-2xl p-6 mb-6" style={{ background: 'linear-gradient(135deg, #fef3e4 0%, #fde9cd 100%)' }}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm font-medium mb-1" style={{ color: '#8B6F47' }}>Current Tier</p>
                        <p className="text-2xl font-bold" style={{ color: '#8B6F47' }}>{currentTier.label}</p>
                      </div>
                      {currentTier.discount > 0 && (
                        <div className="text-right">
                          <p className="text-sm font-medium mb-1" style={{ color: '#8B6F47' }}>Discount</p>
                          <p className="text-2xl font-bold" style={{ color: '#8B6F47' }}>{currentTier.discount}% OFF</p>
                        </div>
                      )}
                    </div>
                    {currentTier.showPrice ? (
                      <div className="pt-4 border-t" style={{ borderColor: '#D4A574' }}>
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm" style={{ color: '#8B6F47' }}>Price per unit:</span>
                          <span className="text-3xl font-bold" style={{ color: '#8B6F47' }}>${currentTier.pricePerUnit.toFixed(2)}</span>
                          {currentTier.discount > 0 && (
                            <span className="text-sm line-through" style={{ color: '#8B6F47' }}>${currentProduct.basePrice}</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="pt-4 border-t" style={{ borderColor: '#D4A574' }}>
                        <p className="text-base font-semibold" style={{ color: '#8B6F47' }}>
                          {currentTier.action === 'quote' ? 'Request Custom Quote' : 'Become a Partner for Special Pricing'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Total */}
                  {currentTier.showPrice && (
                    <div className="bg-gray-50 rounded-2xl p-6 mb-8">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Subtotal ({quantity} units)</span>
                        <span className="text-xl font-bold">${calculateTotal()}</span>
                      </div>
                      {currentTier.discount > 0 && (
                        <div className="flex items-center justify-between text-green-600">
                          <span className="text-sm">You save</span>
                          <span className="text-lg font-bold">${calculateSavings()}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* CTA */}
                  <div className="space-y-3">
                    <Button 
                      onClick={handleCTAClick}
                      className="w-full text-white py-4 rounded-full text-lg font-medium"
                      style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
                    >
                      {currentTier.action === 'quote' ? 'Request Quote' : 'View Product Details'}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-xl font-semibold text-gray-900 mb-2">Coming Q2 2026</p>
                  <p className="text-gray-600 mb-6">This product will be available Q2 2026</p>
                  <Button 
                    onClick={() => setSelectedProduct('onyx-lighting')}
                    className="text-white px-6 py-3 rounded-full"
                    style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
                  >
                    View Available Products
                  </Button>
                </div>
              )}
            </div>

            {/* Pricing Tiers Table */}
            <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
                  <TrendingDown className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold" style={{ color: '#1d1d1f' }}>Volume Discounts</h2>
              </div>

              <div className="space-y-4">
                {pricingTiers.map((tier, idx) => (
                  <div 
                    key={idx}
                    className={`p-6 rounded-2xl border-2 transition-all ${
                      currentTier === tier 
                        ? 'bg-amber-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ borderColor: currentTier === tier ? '#D4A574' : undefined }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-bold text-lg" style={{ color: '#1d1d1f' }}>{tier.label}</p>
                        <p className="text-sm text-gray-600">
                          {tier.max === null ? `${tier.min}+` : `${tier.min} - ${tier.max}`} units
                        </p>
                      </div>
                      <div className="text-right">
                        {tier.showPrice ? (
                          <>
                            <p className="text-2xl font-bold" style={{ color: '#8B6F47' }}>${tier.pricePerUnit.toFixed(2)}</p>
                            <p className="text-sm text-gray-600">per unit</p>
                          </>
                        ) : (
                          <p className="text-base font-semibold" style={{ color: '#8B6F47' }}>
                            {tier.action === 'quote' ? 'Request Quote' : 'Contact Us'}
                          </p>
                        )}
                      </div>
                    </div>
                    {tier.discount > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold" style={{ background: '#dcfce7', color: '#166534' }}>
                          <TrendingDown className="w-4 h-4" />
                          Save {tier.discount}%
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 rounded-2xl" style={{ background: '#fef3e4' }}>
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong style={{ color: '#8B6F47' }}>Custom Pricing Available:</strong> Need 200+ units? Request a custom quote for Partner pricing (200-499 units) or Distributor pricing (500+ units) tailored to your business needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
            Need Help with Your Order?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Our team is ready to assist with custom quotes, product selection, and volume orders.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:info@onyx-homesolutions.com" className="inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-full font-medium transition-colors" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
              <Mail className="w-5 h-5" />
              Email Us
            </a>
            <a href="tel:519-996-2866" className="inline-flex items-center justify-center gap-2 border-2 px-6 py-3 rounded-full font-medium transition-colors hover:bg-amber-50" style={{ borderColor: '#8B6F47', color: '#8B6F47' }}>
              <Phone className="w-5 h-5" />
              Call: 519-996-2866
            </a>
          </div>
        </div>
      </section>

      {/* Email Popup Modal */}
      {showEmailPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative">
            <button 
              onClick={() => setShowEmailPopup(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-2xl font-bold mb-2" style={{ color: '#1d1d1f' }}>Request Custom Quote</h3>
            <p className="text-gray-600 mb-6">Fill out the form below and we&apos;ll get back to you within 24 hours.</p>
            
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input 
                    type="text"
                    name="name"
                    value={emailForm.name}
                    onChange={handleEmailFormChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                    placeholder="John Smith"
                    style={{ borderColor: '#D4A574' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input 
                    type="email"
                    name="email"
                    value={emailForm.email}
                    onChange={handleEmailFormChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                    placeholder="john@company.com"
                    style={{ borderColor: '#D4A574' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Phone *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input 
                    type="tel"
                    name="phone"
                    value={emailForm.phone}
                    onChange={handleEmailFormChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                    placeholder="(555) 123-4567"
                    style={{ borderColor: '#D4A574' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Quantity Needed *</label>
                <input 
                  type="number"
                  name="quantity"
                  value={emailForm.quantity}
                  onChange={handleEmailFormChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                  placeholder="Enter quantity"
                  style={{ borderColor: '#D4A574' }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Additional Details</label>
                <textarea 
                  name="message"
                  value={emailForm.message}
                  onChange={handleEmailFormChange}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none resize-none"
                  placeholder="Any specific requirements or questions..."
                  style={{ borderColor: '#D4A574' }}
                ></textarea>
              </div>

              <Button 
                type="submit"
                className="w-full text-white py-3 rounded-full text-base font-medium"
                style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
              >
                Send Quote Request
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-12 px-4 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <p className="text-gray-400 text-sm">Premium LED lighting and HVAC solutions for modern homes.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-gray-400 hover:text-white">ONYX Lighting</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-gray-400 hover:text-white">ONYX HVAC</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-gray-400 hover:text-white">About Us</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="text-gray-400 hover:text-white">Become a Partner</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="text-gray-400 hover:text-white">Guides</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:info@onyx-homesolutions.com" className="text-gray-400 hover:text-white">info@onyx-homesolutions.com</a></li>
                <li><a href="tel:519-996-2866" className="text-gray-400 hover:text-white">519-996-2866</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            <p>© 2025 ONYX Home Solutions. All rights reserved. | Powered by Syntrexe</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BulkPricingPage;
